import { Navigate, createBrowserRouter } from "react-router-dom";
import { AppShell } from "@/components/layout/app-shell";
import { ModuleNotFoundPage } from "@/components/module-not-found-page";
import { LoginPage } from "@/features/auth/login-page";
import { ProtectedRoute } from "@/features/auth/protected-route";
import { AuthGateLayout } from "@/features/auth/auth-gate-layout";
import { RequestAccessPage } from "@/features/auth/request-access-page";
import { RequestAccessSuccessPage } from "@/features/auth/request-access-success-page";
import { AccessDeniedPage } from "@/features/auth/access-denied-page";
import { NotInvitedPage } from "@/features/auth/not-invited-page";
import { BetaOnboardingPage } from "@/features/auth/beta-onboarding-page";
import { AdminPage } from "@/features/auth/admin-page";
import { BetaAccessAdminPage } from "@/features/beta-access-admin";
import { PlatformFeaturesPage } from "@/features/settings/platform-features-page";
import { useCanAccessWorkerDocuments } from "@/features/auth/use-platform-features";
import { DashboardRouterPage } from "@/features/dashboard";
import { DashboardSetupPage } from "@/features/beta-onboarding";
import { DailyEntryPage } from "@/features/daily-entry";
import {
  DemoShell,
  DemoDashboardPage,
  DemoDailyEntryPage,
  DemoEquipmentPage,
  DemoPayPeriodReviewPage,
  DemoReportsPage,
} from "@/features/demo";
import { OnboardingPage } from "@/features/onboarding";
import { LandingPage } from "@/features/marketing/landing-page";
import { GetStartedCtaPage } from "@/features/marketing/get-started-cta-page";
import {
  CrewsListPage,
  CrewCreatePage,
  CrewDetailPage,
  CrewEditPage,
} from "@/features/crews";
import { PayRulesPage } from "@/features/pay-rules/pay-rules-page";
import {
  EquipmentListPage,
  EquipmentCreatePage,
  EquipmentDetailPage,
  EquipmentEditPage,
  EquipmentImportPage,
} from "@/features/equipment";
import { SitesListPage, SiteCreatePage, SiteDetailPage, SiteEditPage } from "@/features/sites";
import {
  WorkersListPage,
  WorkerCreatePage,
  WorkerDetailPage,
  WorkerEditPage,
  WorkerImportPage,
  WorkerDocumentsPage,
} from "@/features/workers";
import {
  PayPeriodsListPage,
  PayPeriodCreatePage,
  PayPeriodDetailPage,
  PayPeriodReviewPage,
} from "@/features/pay-periods";
import { ExportsPage } from "@/features/exports/exports-page";
import { ReportsPage } from "@/features/reports";
import { FieldClockPage } from "@/features/field";
import { AuditLogsPage } from "@/features/audit-logs";
import { BillingPage } from "@/features/billing";
import { SettingsPage } from "@/features/settings/settings-page";
import { DocumentSecuritySettingsPage } from "@/features/settings/document-security-settings-page";
import {
  DpaPage,
  PrivacyPage,
  SecurityPage,
  TermsPage,
} from "@/features/legal/legal-pages";

function WorkerDocumentsGate() {
  const canAccess = useCanAccessWorkerDocuments();
  if (!canAccess) return <ModuleNotFoundPage />;
  return <WorkerDocumentsPage />;
}

function DocumentSecurityGate() {
  const canAccess = useCanAccessWorkerDocuments();
  if (!canAccess) return <ModuleNotFoundPage />;
  return <DocumentSecuritySettingsPage />;
}

export const router = createBrowserRouter([
  {
    path: "/",
    element: <LandingPage />,
  },
  {
    path: "/demo",
    element: <DemoShell />,
    children: [
      { index: true, element: <Navigate to="/demo/dashboard" replace /> },
      { path: "dashboard", element: <DemoDashboardPage /> },
      { path: "daily-entry", element: <DemoDailyEntryPage /> },
      { path: "equipment", element: <DemoEquipmentPage /> },
      { path: "pay-period-review", element: <DemoPayPeriodReviewPage /> },
      { path: "reports", element: <DemoReportsPage /> },
    ],
  },
  {
    path: "/get-started",
    element: <GetStartedCtaPage />,
  },
  {
    path: "/login",
    element: <LoginPage />,
  },
  { path: "/legal/privacy", element: <PrivacyPage /> },
  { path: "/legal/terms", element: <TermsPage /> },
  { path: "/legal/dpa", element: <DpaPage /> },
  { path: "/legal/security", element: <SecurityPage /> },
  {
    path: "/legal/document-retention",
    element: (
      <main className="mx-auto flex min-h-screen max-w-3xl flex-col items-center justify-center px-6 py-16 text-center">
        <h1 className="text-2xl font-bold text-navy-deep">Page not found</h1>
        <p className="mt-2 text-sm text-muted-foreground">
          The page you requested does not exist or is not available.
        </p>
      </main>
    ),
  },
  {
    path: "/request-access",
    element: <RequestAccessPage />,
  },
  {
    path: "/request-access/success",
    element: <RequestAccessSuccessPage />,
  },
  {
    element: <AuthGateLayout />,
    children: [
      { path: "access-denied", element: <AccessDeniedPage /> },
      { path: "not-invited", element: <NotInvitedPage /> },
      { path: "onboarding/beta", element: <BetaOnboardingPage /> },
      { path: "onboarding", element: <OnboardingPage /> },
      { path: "admin", element: <AdminPage /> },
      { path: "admin/beta-access", element: <BetaAccessAdminPage /> },
      { path: "admin/platform/features", element: <PlatformFeaturesPage /> },
      {
        element: <ProtectedRoute />,
        children: [
          {
            element: <AppShell />,
            children: [
          { path: "dashboard", element: <DashboardRouterPage /> },
          { path: "dashboard/setup", element: <DashboardSetupPage /> },
          { path: "daily-entry", element: <DailyEntryPage /> },
          { path: "time-entries", element: <DailyEntryPage /> },
          { path: "field/clock", element: <FieldClockPage /> },
          { path: "pay-rules", element: <PayRulesPage /> },
          { path: "equipment", element: <EquipmentListPage /> },
          { path: "equipment/import", element: <EquipmentImportPage /> },
          { path: "equipment/new", element: <EquipmentCreatePage /> },
          { path: "equipment/:equipmentId", element: <EquipmentDetailPage /> },
          { path: "equipment/:equipmentId/edit", element: <EquipmentEditPage /> },
          { path: "sites", element: <SitesListPage /> },
          { path: "sites/new", element: <SiteCreatePage /> },
          { path: "sites/:siteId", element: <SiteDetailPage /> },
          { path: "sites/:siteId/edit", element: <SiteEditPage /> },
          { path: "workers", element: <WorkersListPage /> },
          { path: "workers/new", element: <WorkerCreatePage /> },
          { path: "workers/import", element: <WorkerImportPage /> },
          { path: "workers/:workerId", element: <WorkerDetailPage /> },
          { path: "workers/:workerId/documents", element: <WorkerDocumentsGate /> },
          { path: "workers/:workerId/edit", element: <WorkerEditPage /> },
          { path: "crews", element: <CrewsListPage /> },
          { path: "crews/new", element: <CrewCreatePage /> },
          { path: "crews/:crewId", element: <CrewDetailPage /> },
          { path: "crews/:crewId/edit", element: <CrewEditPage /> },
          { path: "pay-periods", element: <PayPeriodsListPage /> },
          { path: "pay-periods/new", element: <PayPeriodCreatePage /> },
          { path: "pay-periods/:payPeriodId", element: <PayPeriodDetailPage /> },
          { path: "pay-periods/:payPeriodId/review", element: <PayPeriodReviewPage /> },
          { path: "reports", element: <ReportsPage /> },
          { path: "exports", element: <ExportsPage /> },
          { path: "billing", element: <BillingPage /> },
          { path: "audit-log", element: <AuditLogsPage /> },
          { path: "audit-logs", element: <Navigate to="/audit-log" replace /> },
          { path: "settings", element: <SettingsPage /> },
          { path: "settings/security/documents", element: <DocumentSecurityGate /> },
            ],
          },
        ],
      },
    ],
  },
]);
